/**
 * Ornstein-Uhlenbeck gust modulation.
 *
 *   dX = theta (mu - X) dt + sigma dW
 *
 * Exact discretization (no time-step bias, valid for any dt):
 *
 *   X(t+dt) = mu + (X(t) - mu) e^(-theta dt)
 *             + sigma * sqrt((1 - e^(-2 theta dt)) / (2 theta)) * Z,   Z ~ N(0, 1)
 *
 * Stationary distribution: N(mu, sigma^2 / (2 theta)).
 *
 * This is the same process as the Vasicek short-rate model:
 * Vasicek, O. (1977). An Equilibrium Characterization of the Term
 * Structure. Journal of Financial Economics, 5(2), 177-188.
 *
 * The windField multiplies turbulence amplitude by max(X, 0). One scalar
 * for the whole storm: gusts are correlated in time, uniform in space.
 */
import type { Rng } from '../core/rng';

export interface OuParams {
  /** Long-run mean. */
  mu: number;
  /** Mean-reversion rate, 1/s. */
  theta: number;
  /** Volatility, units of X per sqrt(s). */
  sigma: number;
}

export interface OuProcess {
  readonly params: OuParams;
  value: number;
  /** Advance by dtS using the exact discretization. Returns the new value. */
  step(dtS: number, rng: Rng): number;
}

/** sigma that gives a target stationary standard deviation for a given theta. */
export function sigmaForStationaryStd(stdDev: number, theta: number): number {
  return stdDev * Math.sqrt(2 * theta);
}

/**
 * One Euler-Maruyama step, for comparison only (the simulation uses the
 * exact step). X + theta (mu - X) dt + sigma sqrt(dt) Z. Its mean after n
 * steps is mu + (x0 - mu)(1 - theta dt)^n instead of the exact
 * mu + (x0 - mu) e^(-theta n dt): biased unless theta dt is small, and
 * unstable (oscillating) once theta dt > 1.
 */
export function eulerMaruyamaStep(x: number, params: OuParams, dtS: number, z: number): number {
  return x + params.theta * (params.mu - x) * dtS + params.sigma * Math.sqrt(dtS) * z;
}

/** One exact step from x with a given standard normal z. */
export function exactOuStep(x: number, params: OuParams, dtS: number, z: number): number {
  const decay = Math.exp(-params.theta * dtS);
  const sd = params.sigma * Math.sqrt((1 - decay * decay) / (2 * params.theta));
  return params.mu + (x - params.mu) * decay + sd * z;
}

export function createOuProcess(params: OuParams, x0: number): OuProcess {
  if (!(params.theta > 0)) throw new RangeError('OU theta must be positive');
  const ou: OuProcess = {
    params,
    value: x0,
    step(dtS: number, rng: Rng): number {
      const { mu, theta, sigma } = params;
      const decay = Math.exp(-theta * dtS);
      const sd = sigma * Math.sqrt((1 - decay * decay) / (2 * theta));
      ou.value = mu + (ou.value - mu) * decay + sd * rng.normal();
      return ou.value;
    },
  };
  return ou;
}
